import React, { useRef, useCallback, useState, useEffect, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { z } from 'zod';
import { useTranslation } from 'next-i18next';
import { LOCALE_HOME_PATHS, Namespaces } from 'types/locales';
import Link from 'components/link';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_LOCAL_FOCUS_CITY_INSIGHTS_NAVIGATE } from 'constants/events';
import ScrollReveal from '../scroll-reveal';
import styles from './style.module.scss';
import parentStyles from '../style.module.scss';
import skeleton from '../skeleton.module.scss';

import type { TFunction } from 'i18next';

// ── Types ────────────────────────────────────────────────────

const groceryChainSchema = z.object({
  name: z.string(),
  locationCount: z.number(),
  logo: z.string().optional(),
});

const foodVenueSchema = z.object({
  name: z.string(),
  type: z.string(),
  rating: z.number(),
});

const schoolSchema = z.object({
  name: z.string(),
  type: z.string(),
  rating: z.number(),
});

/** Validates the `/api/city-insights/[slug]` response before any component consumes it. */
export const cityInsightsDataSchema = z.object({
  city: z.string(),
  // `aiOverview` and `overview` are the AI-generated prose that gets translated
  // per locale. They are optional at render time: when a translated locale has
  // no translation yet, locale selection strips them so the Overview card and
  // data-sources popup are hidden while the locale-independent numeric cards
  // (commute, amenities, groceries, …) still render. See `localize.ts`.
  aiOverview: z.object({
    disclaimer: z.string(),
    sources: z.array(z.string()),
  }).optional(),
  overview: z.object({
    title: z.string(),
    description: z.string(),
    tags: z.array(z.string()),
  }).optional(),
  commuteScores: z.object({
    walkScore: z.number(),
    transitScore: z.number(),
    bikeScore: z.number(),
  }),
  amenities: z.object({
    parks: z.number(),
    fitnessCentres: z.number(),
    libraries: z.number(),
  }),
  groceries: z.object({
    totalStores: z.number(),
    majorChains: z.array(groceryChainSchema),
  }),
  foodAndDrink: z.object({
    totalVenues: z.number(),
    topVenues: z.array(foodVenueSchema),
  }),
  schools: z.object({
    totalSchools: z.number(),
    topSchools: z.array(schoolSchema),
  }),
});

export type CityInsightsData = z.infer<typeof cityInsightsDataSchema>;

interface CityInsightsProps {
  locationName: string;
  isLoading?: boolean;
  /** True while insights are being generated + polled for. Renders the skeleton loading state. */
  isGenerating?: boolean;
  data: CityInsightsData | null;
  /**
   * Pre-computed URL for the current area page. Omitted when the location should read as
   * plain text — AreaPageV2's Insights tab is already that area's page, so a link there
   * went nowhere new and only added a hover underline mid-heading.
   */
  locationUrl?: string;
  /** Optional supporting copy rendered directly below the section heading. */
  description?: string;
}

// ── Score label helpers ──────────────────────────────────────

function getWalkLabel(score: number, t: TFunction): string {
  if (score >= 90) return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_WALK_PARADISE, { defaultValue: "Walker's Paradise" });
  if (score >= 70) return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_WALK_VERY, { defaultValue: 'Very Walkable' });
  if (score >= 50) return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_WALK_SOMEWHAT, { defaultValue: 'Somewhat Walkable' });
  return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_WALK_CAR, { defaultValue: 'Car-Dependent' });
}

function getTransitLabel(score: number, t: TFunction): string {
  if (score >= 70) return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_TRANSIT_EXCELLENT, { defaultValue: 'Excellent Transit' });
  if (score >= 50) return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_TRANSIT_GOOD, { defaultValue: 'Good Transit' });
  if (score >= 25) return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_TRANSIT_SOME, { defaultValue: 'Some Transit' });
  return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_TRANSIT_MINIMAL, { defaultValue: 'Minimal Transit' });
}

function getBikeLabel(score: number, t: TFunction): string {
  if (score >= 90) return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_BIKE_PARADISE, { defaultValue: "Biker's Paradise" });
  if (score >= 70) return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_BIKE_VERY, { defaultValue: 'Very Bikeable' });
  if (score >= 50) return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_BIKE_BIKEABLE, { defaultValue: 'Bikeable' });
  return t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_BIKE_SOMEWHAT, { defaultValue: 'Somewhat Bikeable' });
}

// ── Tag color presets (cycled by index) ──────────────────────

const TAG_STYLES = [
  { bg: '#eff6ff', border: '#dbeafe', color: '#1d4ed8' },
  { bg: '#e8f7ed', border: 'rgba(16,185,129,0.1)', color: '#059669' },
  { bg: '#faf5ff', border: '#f3e8ff', color: '#7e22ce' },
  { bg: '#fff7ed', border: '#ffedd5', color: '#c2410c' },
];

// ── SVG Icons ────────────────────────────────────────────────

const Icons = {
  walk: (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="22" viewBox="0 0 13 22" fill="none">
      <path d="M1 21.5L3.8 7.4L2 8.1V11.5H0V6.8L5.05 4.65C5.28333 4.55 5.52917 4.49167 5.7875 4.475C6.04583 4.45833 6.29167 4.49167 6.525 4.575C6.75833 4.65833 6.97917 4.775 7.1875 4.925C7.39583 5.075 7.56667 5.26667 7.7 5.5L8.7 7.1C9.13333 7.8 9.72083 8.375 10.4625 8.825C11.2042 9.275 12.05 9.5 13 9.5V11.5C11.8333 11.5 10.7917 11.2583 9.875 10.775C8.95833 10.2917 8.175 9.675 7.525 8.925L6.9 12L9 14V21.5H7V15L4.9 13.4L3.1 21.5H1ZM7.5 4C6.95 4 6.47917 3.80417 6.0875 3.4125C5.69583 3.02083 5.5 2.55 5.5 2C5.5 1.45 5.69583 0.979167 6.0875 0.5875C6.47917 0.195833 6.95 0 7.5 0C8.05 0 8.52083 0.195833 8.9125 0.5875C9.30417 0.979167 9.5 1.45 9.5 2C9.5 2.55 9.30417 3.02083 8.9125 3.4125C8.52083 3.80417 8.05 4 7.5 4Z" fill="#CBD5E1"/>
    </svg>
  ),
  transit: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="19" viewBox="0 0 16 19" fill="none">
      <path d="M0 13.5V4C0 3.11667 0.229167 2.4125 0.6875 1.8875C1.14583 1.3625 1.75 0.9625 2.5 0.6875C3.25 0.4125 4.10417 0.229167 5.0625 0.1375C6.02083 0.0458333 7 0 8 0C9.1 0 10.1375 0.0458333 11.1125 0.1375C12.0875 0.229167 12.9375 0.4125 13.6625 0.6875C14.3875 0.9625 14.9583 1.3625 15.375 1.8875C15.7917 2.4125 16 3.11667 16 4V13.5C16 14.4833 15.6625 15.3125 14.9875 15.9875C14.3125 16.6625 13.4833 17 12.5 17L14 18.5V19H12L10 17H6L4 19H2V18.5L3.5 17C2.51667 17 1.6875 16.6625 1.0125 15.9875C0.3375 15.3125 0 14.4833 0 13.5ZM8 2C6.23333 2 4.94167 2.10417 4.125 2.3125C3.30833 2.52083 2.75 2.75 2.45 3H13.65C13.4 2.71667 12.8625 2.47917 12.0375 2.2875C11.2125 2.09583 9.86667 2 8 2ZM2 8H7V5H2V8ZM12.5 10H3.5C3.06667 10 2.70833 10 2.425 10C2.14167 10 2 10 2 10H14C14 10 13.8583 10 13.575 10C13.2917 10 12.9333 10 12.5 10ZM9 8H14V5H9V8ZM4.5 14C4.93333 14 5.29167 13.8583 5.575 13.575C5.85833 13.2917 6 12.9333 6 12.5C6 12.0667 5.85833 11.7083 5.575 11.425C5.29167 11.1417 4.93333 11 4.5 11C4.06667 11 3.70833 11.1417 3.425 11.425C3.14167 11.7083 3 12.0667 3 12.5C3 12.9333 3.14167 13.2917 3.425 13.575C3.70833 13.8583 4.06667 14 4.5 14ZM11.5 14C11.9333 14 12.2917 13.8583 12.575 13.575C12.8583 13.2917 13 12.9333 13 12.5C13 12.0667 12.8583 11.7083 12.575 11.425C12.2917 11.1417 11.9333 11 11.5 11C11.0667 11 10.7083 11.1417 10.425 11.425C10.1417 11.7083 10 12.0667 10 12.5C10 12.9333 10.1417 13.2917 10.425 13.575C10.7083 13.8583 11.0667 14 11.5 14ZM3.5 15H12.5C12.9333 15 13.2917 14.8583 13.575 14.575C13.8583 14.2917 14 13.9333 14 13.5V10H2V13.5C2 13.9333 2.14167 14.2917 2.425 14.575C2.70833 14.8583 3.06667 15 3.5 15ZM8 3C9.86667 3 11.2125 3 12.0375 3C12.8625 3 13.4 3 13.65 3H2.45C2.75 3 3.30833 3 4.125 3C4.94167 3 6.23333 3 8 3Z" fill="#CBD5E1"/>
    </svg>
  ),
  bike: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
      <path d="M12.9167 3.33333C13.8333 3.33333 14.5833 2.58333 14.5833 1.66667C14.5833 0.75 13.8333 0 12.9167 0C12 0 11.25 0.75 11.25 1.66667C11.25 2.58333 12 3.33333 12.9167 3.33333ZM4.16667 8.75C1.83333 8.75 0 10.5833 0 12.9167C0 15.25 1.83333 17.0833 4.16667 17.0833C6.5 17.0833 8.33333 15.25 8.33333 12.9167C8.33333 10.5833 6.5 8.75 4.16667 8.75ZM4.16667 15.8333C2.58333 15.8333 1.25 14.5 1.25 12.9167C1.25 11.3333 2.58333 10 4.16667 10C5.75 10 7.08333 11.3333 7.08333 12.9167C7.08333 14.5 5.75 15.8333 4.16667 15.8333ZM9 7.5L11 5.5L11.6667 6.16667C12.75 7.25 14.1667 7.91667 15.9167 7.91667V6.25C14.6667 6.25 13.6667 5.75 12.9167 5L11.3333 3.41667C10.9167 3.08333 10.5 2.91667 10 2.91667C9.5 2.91667 9.08333 3.08333 8.83333 3.41667L6.5 5.75C6.16667 6.08333 6 6.5 6 6.91667C6 7.41667 6.16667 7.83333 6.5 8.08333L9.16667 10.4167V14.5833H10.8333V9.41667L9 7.5ZM15.8333 8.75C13.5 8.75 11.6667 10.5833 11.6667 12.9167C11.6667 15.25 13.5 17.0833 15.8333 17.0833C18.1667 17.0833 20 15.25 20 12.9167C20 10.5833 18.1667 8.75 15.8333 8.75ZM15.8333 15.8333C14.25 15.8333 12.9167 14.5 12.9167 12.9167C12.9167 11.3333 14.25 10 15.8333 10C17.4167 10 18.75 11.3333 18.75 12.9167C18.75 14.5 17.4167 15.8333 15.8333 15.8333Z" fill="#CBD5E1"/>
    </svg>
  ),
  park: (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none">
      <path d="M13.9 10H15.9L8.9 0L1.95 10H3.9L0 16H6.92V20H10.87V16H17.9L13.9 10ZM3.69 14L7.59 8H5.78L8.91 3.5L12.06 8H10.16L14.16 14H3.69Z" fill="#10B981"/>
    </svg>
  ),
  fitness: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M18.625 7.38777L17.225 5.98777L17.975 5.21277L14.775 2.01277L14 2.76277L12.575 1.33777L13.325 0.562766C13.7083 0.179433 14.1833 -0.00806738 14.75 0.000265957C15.3167 0.00859929 15.7917 0.204433 16.175 0.587766L19.4 3.81277C19.7833 4.1961 19.975 4.66693 19.975 5.22527C19.975 5.7836 19.7833 6.25443 19.4 6.63777L18.625 7.38777ZM6.625 19.4128C6.24167 19.7961 5.77083 19.9878 5.2125 19.9878C4.65417 19.9878 4.18333 19.7961 3.8 19.4128L0.575 16.1878C0.191667 15.8044 0 15.3336 0 14.7753C0 14.2169 0.191667 13.7461 0.575 13.3628L1.325 12.6128L2.75 14.0378L1.975 14.7878L5.2 18.0128L5.95 17.2378L7.375 18.6628L6.625 19.4128ZM16.55 11.0128L17.975 9.58777L10.4 2.01277L8.975 3.43777L16.55 11.0128ZM9.55 18.0128L10.975 16.5628L3.425 9.01277L1.975 10.4378L9.55 18.0128ZM9.4 12.1628L12.15 9.43777L10.55 7.83777L7.825 10.5878L9.4 12.1628ZM10.975 19.4128C10.5917 19.7961 10.1167 19.9878 9.55 19.9878C8.98333 19.9878 8.50833 19.7961 8.125 19.4128L0.575 11.8628C0.191667 11.4794 0 11.0044 0 10.4378C0 9.8711 0.191667 9.3961 0.575 9.01277L2 7.58777C2.38333 7.20443 2.85417 7.01277 3.4125 7.01277C3.97083 7.01277 4.44167 7.20443 4.825 7.58777L6.4 9.16277L9.15 6.41277L7.575 4.86277C7.19167 4.47943 7 4.00443 7 3.43777C7 2.8711 7.19167 2.3961 7.575 2.01277L9 0.587766C9.38333 0.204433 9.85417 0.012766 10.4125 0.012766C10.9708 0.012766 11.4417 0.204433 11.825 0.587766L19.4 8.16277C19.7833 8.5461 19.975 9.01693 19.975 9.57527C19.975 10.1336 19.7833 10.6044 19.4 10.9878L17.975 12.4128C17.5917 12.7961 17.1167 12.9878 16.55 12.9878C15.9833 12.9878 15.5083 12.7961 15.125 12.4128L13.575 10.8378L10.825 13.5878L12.4 15.1628C12.7833 15.5461 12.975 16.0169 12.975 16.5753C12.975 17.1336 12.7833 17.6044 12.4 17.9878L10.975 19.4128Z" fill="#137FEC"/>
    </svg>
  ),
  library: (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22" fill="none">
      <path d="M9 8C11.21 8 13 6.21 13 4C13 1.79 11.21 0 9 0C6.79 0 5 1.79 5 4C5 6.21 6.79 8 9 8ZM9 2C10.1 2 11 2.9 11 4C11 5.1 10.1 6 9 6C7.9 6 7 5.1 7 4C7 2.9 7.9 2 9 2ZM9 10.55C6.64 8.35 3.48 7 0 7V18C3.48 18 6.64 19.35 9 21.55C11.36 19.36 14.52 18 18 18V7C14.52 7 11.36 8.35 9 10.55ZM16 16.13C13.47 16.47 11.07 17.43 9 18.95C6.94 17.43 4.53 16.46 2 16.12V9.17C4.1 9.55 6.05 10.52 7.64 12L9 13.28L10.36 12.01C11.95 10.53 13.9 9.56 16 9.18V16.13Z" fill="#4F46E5"/>
    </svg>

  ),
  restaurant: (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="17" viewBox="0 0 15 17" fill="none">
      <path d="M10.8333 3.33333V10H13.3333V16.6667H15V0C12.7 0 10.8333 1.86667 10.8333 3.33333ZM6.66667 5.83333H5V0H3.33333V5.83333H1.66667V0H0V5.83333C0 7.675 1.49167 9.16667 3.33333 9.16667V16.6667H5V9.16667C6.84167 9.16667 8.33333 7.675 8.33333 5.83333V0H6.66667V5.83333Z" fill="#1B489B"/>
    </svg>
  ),
  bar: (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="18" viewBox="0 0 12 18" fill="none">
      <path d="M0 0V6C0 8.97 2.16 11.43 5 11.91V16H2V18H10V16H7V11.91C9.84 11.43 12 8.97 12 6V0H0ZM6 10C4.14 10 2.59 8.72 2.14 7H9.86C9.41 8.72 7.86 10 6 10ZM10 5H2V2H10V5Z" fill="#1B489B"/>
    </svg>
  ),
  cafe: (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path d="M12.0833 0H1.66667C0.75 0 0 0.75 0 1.66667V6.425C0 9.61667 2.45833 12.4083 5.65 12.5C8.95 12.6 11.6667 9.95 11.6667 6.66667V5.83333H12.0833C13.6917 5.83333 15 4.525 15 2.91667C15 1.30833 13.6917 0 12.0833 0ZM10 1.66667V4.16667H1.66667V1.66667H10ZM10 5.83333V6.66667C10 8.96667 8.13333 10.8333 5.83333 10.8333C3.53333 10.8333 1.66667 8.96667 1.66667 6.66667V5.83333M12.0833 4.16667H11.6667V1.66667H12.0833C12.775 1.66667 13.3333 2.225 13.3333 2.91667C13.3333 3.60833 12.775 4.16667 12.0833 4.16667ZM0 13.3333H13.3333V15H0V13.3333Z" fill="#1B489B"/>
    </svg>
  ),
  info: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#64748b" strokeWidth="1.5"/><path d="M12 16v-4M12 8h.01" stroke="#64748b" strokeWidth="2" strokeLinecap="round"/></svg>
  ),
  close: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  chevronLeft: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  chevronRight: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  grocery: (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
      <path d="M5 13.3333C4.08333 13.3333 3.34167 14.0833 3.34167 15C3.34167 15.9167 4.08333 16.6667 5 16.6667C5.91667 16.6667 6.66667 15.9167 6.66667 15C6.66667 14.0833 5.91667 13.3333 5 13.3333ZM13.3333 13.3333C12.4167 13.3333 11.675 14.0833 11.675 15C11.675 15.9167 12.4167 16.6667 13.3333 16.6667C14.25 16.6667 15 15.9167 15 15C15 14.0833 14.25 13.3333 13.3333 13.3333ZM12.125 9.16667C12.75 9.16667 13.3 8.825 13.5833 8.30833L16.5667 2.9C16.875 2.35 16.475 1.66667 15.8417 1.66667H3.50833L2.725 0H0V1.66667H1.66667L4.66667 7.99167L3.54167 10.025C2.93333 11.1417 3.73333 12.5 5 12.5H15V10.8333H5L5.91667 9.16667H12.125ZM4.3 3.33333H14.425L12.125 7.5H6.275L4.3 3.33333Z" fill="#1B489B"/>
    </svg>
  ),
};

const VENUE_TYPE_ICONS: Record<string, React.ReactNode> = {
  bar: Icons.bar,
  café: Icons.cafe,
  cafe: Icons.cafe,
  coffee: Icons.cafe,
};

function getVenueIcon(type: string): React.ReactNode {
  const key = type.toLowerCase();
  return VENUE_TYPE_ICONS[key] ?? Icons.restaurant;
}

// ── Grocery logo with fallback ───────────────────────────────

function GroceryLogo({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return <span className={styles['list-avatar-icon']}>{Icons.grocery}</span>;
  }

  return (
    <img
      className={styles['list-avatar-img']}
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
    />
  );
}

// ── Mobile viewport subscription (useSyncExternalStore) ──────

const MOBILE_MEDIA_QUERY = '(max-width: 600px)';

function subscribeMobile(callback: () => void) {
  const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

function getMobileSnapshot() {
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

function getMobileServerSnapshot() {
  return false;
}

// ── Component ────────────────────────────────────────────────

export default function CityInsights({ locationName, isLoading, isGenerating, data, locationUrl, description }: CityInsightsProps) {
  const { t } = useTranslation(Namespaces.HOME);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const isMobile = useSyncExternalStore(subscribeMobile, getMobileSnapshot, getMobileServerSnapshot);
  const infoRef = useRef<HTMLDivElement>(null);
  const canHover = useRef(false);
  // Timestamp of the last programmatic (arrow) scroll, so the resulting smooth
  // scroll isn't double-counted as a manual swipe by the scroll handler below.
  const lastArrowScrollRef = useRef(0);

  useEffect(() => {
    canHover.current = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }, []);

  const scroll = useCallback((dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    lastArrowScrollRef.current = Date.now();
    AnalyticsController.trackEvent(GTM_LOCAL_FOCUS_CITY_INSIGHTS_NAVIGATE, 'All', 'arrow');
    scrollRef.current.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' });
  }, []);

  // Track manual swipe/drag scrolling of the carousel, debounced so it fires
  // once per gesture. Programmatic arrow scrolls are ignored (see ref above).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let debounceId: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        // Skip if this scroll was triggered by an arrow click (smooth scroll).
        if (Date.now() - lastArrowScrollRef.current < 900) return;
        AnalyticsController.trackEvent(GTM_LOCAL_FOCUS_CITY_INSIGHTS_NAVIGATE, 'All', 'swipe');
      }, 200);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (debounceId) clearTimeout(debounceId);
    };
  }, [data]);

  const handleInfoEnter = useCallback(() => {
    setIsInfoOpen(true);
  }, []);

  const handleInfoLeave = useCallback(() => {
    setIsInfoOpen(false);
  }, []);

  const closeInfo = useCallback(() => setIsInfoOpen(false), []);

  if (isGenerating && !data) {
    const loadingCards = [
      t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_COMMUTE_FACTS, { defaultValue: 'Commute Facts' }),
      t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_TOP_AMENITIES, { defaultValue: 'Top Amenities' }),
      t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_GROCERIES, { defaultValue: 'Groceries' }),
      t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_FOOD_AND_DRINKS, { defaultValue: 'Food & Drinks' }),
      t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_TOP_SCHOOLS, { defaultValue: 'Top Schools' }),
    ];

    return (
      <div className={styles['city-insights']}>
        <ScrollReveal className={parentStyles['heading-dropdown-layer']}>
          <div className={parentStyles['section-heading']}>
            <span className={parentStyles['heading-text']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_DEEP_DIVE, { defaultValue: 'Deep Dive: ' })}</span>
            {locationName && <span className={parentStyles['heading-text']}>{locationName}</span>}
          </div>
          {description && <p className={styles['section-description']}>{description}</p>}
        </ScrollReveal>

        <div className={styles['cards-track']}>
          <div className={`${styles.card} ${styles['overview-card']}`}>
            <h3 className={styles['card-title']}>
              {t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_GENERATING_INSIGHTS_TITLE, { location: locationName, defaultValue: 'Generating Insights for {{location}}' })}
            </h3>
            <div className={styles['overview-body']}>
              <p className={styles['overview-desc']}>
                {t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_GENERATING_INSIGHTS_DESC, { defaultValue: 'Analyzing local walkability, top amenities, and dining highlights in real-time' })}
              </p>
            </div>
          </div>
          {loadingCards.map(title => (
            <div key={title} className={styles.card}>
              <div className={styles['card-inner']}>
                <h3 className={styles['card-title']}>{title}</h3>
                <div className={styles['loading-rows']}>
                  {[0, 1, 2].map(i => (
                    <div key={i} className={styles['loading-row']}>
                      <span className={skeleton['bone-round']} style={{ width: 44, height: 44, flexShrink: 0 }} />
                      <div className={styles['loading-lines']}>
                        <span className={skeleton.bone} style={{ height: 12, width: '70%' }} />
                        <span className={skeleton.bone} style={{ height: 10, width: '45%' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;
  if (isLoading) return null;

  const { aiOverview, overview, commuteScores, amenities, groceries, foodAndDrink, schools } = data;

  const popupContent = isInfoOpen && aiOverview ? (
    <div className={styles['info-popup']}>
      <div className={styles['info-drawer-handle']} />
      <div className={styles['info-popup-header']}>
        <span className={styles['info-popup-title']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_AI_RESULTS, { defaultValue: 'AI Gemini results' })}</span>
        <button
          className={styles['info-popup-close']}
          onClick={e => { e.stopPropagation(); closeInfo(); }}
          aria-label={t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_CLOSE, { defaultValue: 'Close' })}
        >
          {Icons.close}
        </button>
      </div>
      <div className={styles['info-popup-body']}>
        <p className={styles['info-popup-disclaimer']}>{aiOverview.disclaimer}</p>
        <ul className={styles['info-popup-sources']}>
          {aiOverview.sources.map((source, i) => (
            <li key={i}>{source}</li>
          ))}
        </ul>
      </div>
    </div>
  ) : null;

  return (
    <div className={styles['city-insights']}>
      <ScrollReveal className={parentStyles['heading-dropdown-layer']}>
        <div className={parentStyles['section-heading']}>
          <span className={parentStyles['heading-text']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_DEEP_DIVE, { defaultValue: 'Deep Dive: ' })}</span>
          {locationName && locationUrl ? (
            <Link href={locationUrl} className={parentStyles['heading-location']}>
              {locationName}
            </Link>
          ) : (
            <span className={parentStyles['heading-text']}>{locationName}</span>
          )}
          {aiOverview && (
            <div
              className={styles['info-wrapper']}
              ref={infoRef}
              onMouseEnter={handleInfoEnter}
              onMouseLeave={handleInfoLeave}
            >
              <button
                className={styles['info-btn']}
                onClick={() => setIsInfoOpen(prev => !prev)}
                aria-label={t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_DATA_SOURCES_INFO, { defaultValue: 'Data sources info' })}
              >
                {Icons.info}
              </button>
              {!isMobile && popupContent}
            </div>
          )}
          <div className={styles['scroll-controls']}>
            <button className={styles['scroll-btn']} onClick={() => scroll('left')} aria-label={t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_SCROLL_LEFT, { defaultValue: 'Scroll left' })}>
              {Icons.chevronLeft}
            </button>
            <button className={styles['scroll-btn']} onClick={() => scroll('right')} aria-label={t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_SCROLL_RIGHT, { defaultValue: 'Scroll right' })}>
              {Icons.chevronRight}
            </button>
          </div>
        </div>
        {description && <p className={styles['section-description']}>{description}</p>}
      </ScrollReveal>

      {isMobile && isInfoOpen && createPortal(
        <div className={styles['info-drawer-portal']}>
          <div className={styles['info-backdrop']} onClick={closeInfo} />
          {popupContent}
        </div>,
        document.body
      )}

      <div className={styles['cards-track']} ref={scrollRef}>
        {/* ── Overview ─────────────────────────────────────── */}
        {/* AI-generated prose — hidden on a translated locale until its
            translation is generated, so it's never shown in English there. */}
        {overview && (
          <div className={`${styles.card} ${styles['overview-card']}`}>
            <h3 className={styles['card-title']}>{overview.title}</h3>
            <div className={styles['overview-body']}>
              <p className={styles['overview-desc']}>{overview.description}</p>
              <div className={styles['overview-tags']}>
                {overview.tags.map((tag, i) => {
                  const palette = TAG_STYLES[i % TAG_STYLES.length];
                  return (
                    <span
                      key={tag}
                      className={styles['overview-tag']}
                      style={{ background: palette.bg, borderColor: palette.border, color: palette.color }}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Commute Facts ────────────────────────────────── */}
        <div className={styles.card}>
          <div className={styles['card-inner']}>
            <h3 className={styles['card-title']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_COMMUTE_FACTS, { defaultValue: 'Commute Facts' })}</h3>
            <div className={styles['commute-scores']}>
              {[
                { key: 'walk', label: t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_WALK_SCORE, { defaultValue: 'Walk Score' }), value: commuteScores.walkScore, descFn: getWalkLabel, ringColor: '#d97706', icon: Icons.walk },
                { key: 'transit', label: t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_TRANSIT_SCORE, { defaultValue: 'Transit Score' }), value: commuteScores.transitScore, descFn: getTransitLabel, ringColor: '#10b981', icon: Icons.transit },
                { key: 'bike', label: t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_BIKE_SCORE, { defaultValue: 'Bike Score' }), value: commuteScores.bikeScore, descFn: getBikeLabel, ringColor: '#137fec', icon: Icons.bike },
              ].map(score => (
                <div key={score.key} className={styles['score-row']}>
                  <div className={styles['score-left']}>
                    <div className={styles['score-ring']} style={{ borderColor: score.ringColor }}>
                      <span className={styles['score-value']}>{score.value}</span>
                    </div>
                    <div className={styles['score-labels']}>
                      <span className={styles['score-name']}>{score.label}</span>
                      <span className={styles['score-desc']}>{score.descFn(score.value, t)}</span>
                    </div>
                  </div>
                  <span className={styles['score-icon']}>{score.icon}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Top Amenities ────────────────────────────────── */}
        <div className={styles.card}>
          <div className={styles['card-inner']}>
            <h3 className={styles['card-title']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_TOP_AMENITIES, { defaultValue: 'Top Amenities' })}</h3>
            <div className={styles['amenity-rows']}>
              {[
                { key: 'parks', icon: Icons.park, label: t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_PARKS, { defaultValue: 'Parks' }), count: amenities.parks },
                { key: 'fitness', icon: Icons.fitness, label: t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_FITNESS_CENTRES, { defaultValue: 'Fitness Centres' }), count: amenities.fitnessCentres },
                { key: 'libraries', icon: Icons.library, label: t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_LIBRARIES, { defaultValue: 'Libraries' }), count: amenities.libraries },
              ].map(item => (
                <div key={item.key} className={styles['amenity-pill']}>
                  <div className={styles['amenity-left']}>
                    <span className={styles['amenity-icon']}>{item.icon}</span>
                    <span className={styles['amenity-label']}>{item.label}</span>
                  </div>
                  <span className={styles['amenity-count']}>{item.count}+</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Groceries ───────────────────────────────────── */}
        <div className={styles.card}>
          <div className={styles['card-inner']}>
            <h3 className={styles['card-title']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_GROCERIES, { defaultValue: 'Groceries' })}</h3>
            <span className={styles['total-badge']}>{groceries.totalStores}+</span>
            <div className={styles['list-items']}>
              {groceries.majorChains.map(chain => (
                <div key={chain.name} className={styles['list-item']}>
                  <div className={styles['list-avatar']}>
                    {chain.logo ? (
                      <GroceryLogo src={chain.logo} alt={chain.name} />
                    ) : (
                      <span className={styles['list-avatar-icon']}>
                        {Icons.grocery}
                      </span>
                    )}
                  </div>
                  <div className={styles['list-text']}>
                    <span className={styles['list-name']}>{chain.name}</span>
                    <span className={styles['list-sub']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_IN_TOTAL, { count: chain.locationCount, defaultValue: '{{count}}+ in total' })}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Food & Drinks ───────────────────────────────── */}
        <div className={styles.card}>
          <div className={styles['card-inner']}>
            <h3 className={styles['card-title']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_FOOD_AND_DRINKS, { defaultValue: 'Food & Drinks' })}</h3>
            <span className={styles['total-badge']}>{foodAndDrink.totalVenues}+</span>
            <div className={styles['list-items']}>
              {foodAndDrink.topVenues.map(venue => (
                <div key={venue.name} className={styles['list-item']}>
                  <div className={styles['list-avatar']}>
                    <span className={styles['list-avatar-icon']}>
                      {getVenueIcon(venue.type)}
                    </span>
                  </div>
                  <div className={styles['list-text']}>
                    <span className={styles['list-name-bold']}>{venue.name}</span>
                    <span className={styles['list-sub']}>{venue.rating} ★ · {venue.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Top Schools ─────────────────────────────────── */}
        <div className={styles.card}>
          <div className={styles['card-inner']}>
            <h3 className={styles['card-title']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_INSIGHTS_TOP_SCHOOLS, { defaultValue: 'Top Schools' })}</h3>
            <span className={styles['total-badge']}>{schools.totalSchools}+</span>
            <div className={styles['school-rows']}>
              {schools.topSchools.map(school => (
                <div key={school.name} className={styles['school-row']}>
                  <div className={styles['school-info']}>
                    <span className={styles['school-name']}>{school.name}</span>
                    <span className={styles['school-type']}>{school.type}</span>
                  </div>
                  <div className={styles['school-rating']}>
                    <span className={styles['rating-text']}>
                      {school.rating}<span className={styles['rating-denom']}>/10</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
